/* eslint-disable no-empty-pattern */
import { QueryFunctionContext } from "react-query";
import {
  ESignSetting,
  EmailCCSettings,
  EmailSetting,
  EmailTemplate,
  EmailTemplateSaveObject,
  EmployeePortalSetting,
  EmployeeHistorySettings,
  GrantLetterSetting,
  GrantLetterTemplate,
  ListOfEmailTemplates,
  TemplateContent,
  ReportSettings,
} from "../types/SiteSettings";
import { UserDetailModel } from "../types/UserDetailModel";
import hissaApi from "./hissaApi";
import optionsApi from "./optionsApi";

export async function getEmployeePortalSettings(): Promise<EmployeePortalSetting> {
  return optionsApi
    .get("v1/settings/getEmployeePortalSettings")
    .then((res) => res.data.data.data);
}
export async function getEmailSettings(): Promise<EmailSetting> {
  return optionsApi
    .get("v1/settings/getEmailSettings")
    .then((res) => res.data.data.emailSettings);
}

export async function getGrantLetterTemplates(): Promise<
  GrantLetterTemplate[]
> {
  return optionsApi
    .get("v1/grant/getGrantLetterTemplates")
    .then((res) => res.data.data);
}

export async function getGrantLetterSettings(): Promise<GrantLetterSetting> {
  return optionsApi
    .get("v1/settings/getGrantLetterSettings")
    .then((res) => res.data.data);
}
export async function getESignSettings(): Promise<ESignSetting> {
  return optionsApi
    .get("v1/settings/getESignSettings")
    .then((res) => res.data.data.esignSettings);
}
export async function setEmployeePortalSettingDetails(
  EmployeePortalSettingDetails: EmployeePortalSetting
): Promise<EmployeePortalSetting> {
  return optionsApi
    .put(
      "v1/settings/updateEmployeePortalSettings",
      EmployeePortalSettingDetails
    )
    .then((res) => res.data);
}

export async function getEmployeeHistorySettings(): Promise<EmployeeHistorySettings> {
  return optionsApi
    .get("v1/settings/getEmployeeHistorySettings")
    .then((res) => res.data.data);
}

export async function setEmployeeHistorySettings(
  settings: EmployeeHistorySettings
): Promise<EmployeeHistorySettings> {
  return optionsApi
    .put("v1/settings/updateEmployeeHistorySettings", settings)
    .then((res) => res.data.data);
}

export async function getReportSettings(): Promise<ReportSettings> {
  return optionsApi
    .get("v1/settings/getReportSettings")
    .then((res) => res.data.data);
}

export async function setReportSettings(
  settings: Partial<ReportSettings>
): Promise<ReportSettings> {
  return optionsApi
    .put("v1/settings/updateReportSettings", settings)
    .then((res) => res.data.data);
}

export async function setEmailSettingDetails(
  EmailSettingDetails: EmailSetting
): Promise<EmailSetting> {
  return optionsApi
    .put("v1/settings/updateEmailSettings", EmailSettingDetails)
    .then((res) => res.data);
}
export async function setGrantLetterSettingDetails(
  GrantLetterSettingDetails: GrantLetterSetting
): Promise<GrantLetterSetting> {
  return optionsApi
    .put("v1/settings/updateGrantLetterSettings", GrantLetterSettingDetails)
    .then((res) => res.data);
}
export async function setDeleteDefaultSignature({}): Promise<any> {
  return optionsApi.delete("/v1/settings/deleteSignature").then((res) => res);
}
export async function setESginSettingDetails(
  ESignSettingDetails: ESignSetting
): Promise<ESignSetting> {
  return optionsApi
    .put("v1/settings/updateESignSettings", ESignSettingDetails)
    .then((res) => res.data);
}

export async function getEmailTemplateNames(): Promise<string[]> {
  return hissaApi.get("email-template/dropdown").then((res) => res.data);
}

export async function getEmailTemplateContent(
  context: QueryFunctionContext
): Promise<EmailTemplate> {
  const query = `v1/settings/getEmailtemplateContent?id=${context.queryKey[1]}`;
  return optionsApi.get(query).then((res) => res.data?.data);
}
export async function getEmailTemplateContentV1(
  templateId: string
): Promise<EmailTemplate> {
  const query = `v1/settings/getEmailtemplateContent?id=${templateId}`;
  return optionsApi.get(query).then((res) => res.data?.data);
}

export async function saveEmailTemplateContent(
  template: EmailTemplateSaveObject
): Promise<string> {
  return optionsApi.post("v1/settings/saveEmailTemplate", template);
}

export async function getAllUser(): Promise<UserDetailModel[]> {
  return hissaApi.get("/user/getAllUser").then((res) => res?.data);
}

export async function listEmailTemplates(): Promise<ListOfEmailTemplates[]> {
  return optionsApi
    .get("v1/settings/listAllEmailTemplates")
    .then((res) => res?.data?.data);
}
export async function setCCEmailSettings(
  ccEmailSetting: EmailCCSettings
): Promise<EmailSetting> {
  return optionsApi
    .put("v1/settings/saveccSettings", ccEmailSetting)
    .then((res) => res.data);
}

export async function bulkAssignGrantLetterTemplate(payload: {
  grantIds: string[];
  templateId?: string;
  isMultipleAssign?: boolean;
  grantIdToTemplateIdMap?: Record<string, string>;
}): Promise<{
  assigned: number;
  errors: { grantId: string; reason: string }[];
  unmatched: string[];
}> {
  return optionsApi
    .post("v1/grant/bulkAssignGrantLetterTemplate", payload)
    .then((res) => res.data.data);
}

export interface BulkUploadGrantLetterTemplatesPayload {
  grantIds?: string[];
  templateId?: string;
  files: { name: string; mimetype: string; data: string }[];
}

export interface UploadGrantLetterTemplateFilesPayload {
  files: { name: string; mimetype: string; data: string }[];
}

export async function uploadGrantLetterTemplateFiles(
  payload: UploadGrantLetterTemplateFilesPayload
): Promise<{
  created: { id: string; templateName: string; s3Path: string }[];
  errors: { fileName: string; reason: string }[];
}> {
  return optionsApi
    .post("v1/grant/uploadGrantLetterTemplateFiles", payload)
    .then((res) => res.data.data);
}

export async function bulkUploadGrantLetterTemplates(
  payload: BulkUploadGrantLetterTemplatesPayload
): Promise<{
  assigned: number;
  created: {
    id: string;
    templateName: string;
    s3Path: string;
  }[];
  unmatched: string[];
  errors: { grantId: string; reason: string }[];
}> {
  return optionsApi
    .post("v1/grant/bulkUploadGrantLetterTemplates", payload)
    .then((res) => res.data.data);
}

export async function deleteGrantLetterTemplate(
  templateId: string
): Promise<void> {
  return optionsApi
    .delete(`v1/grant/deleteGrantLetterTemplate?templateId=${templateId}`)
    .then(() => undefined);
}

export async function getCCEmailSettings(): Promise<EmailCCSettings> {
  return optionsApi
    .get("v1/settings/getccSettings")
    .then((res) => res.data.data);
}

export async function deleteEmailTemplate(templateId: string) {
  return optionsApi.delete(
    `v1//settings/deleteTemplate?templateId=${templateId}`
  );
}

export interface EmailTemplatePreviewResponse {
  html: string;
  kind?: "html" | "amp";
  ampHtml?: string;
  interactive?: boolean;
}

export async function getEmailTemplatePreview(
  templateType: string
): Promise<EmailTemplatePreviewResponse> {
  return optionsApi
    .get("v1/settings/email-template/preview", { params: { templateType } })
    .then((res) => res.data.data);
}

export async function previewEmailTemplateBody(
  templateBodyBase64: string,
  templateType: string
): Promise<EmailTemplatePreviewResponse> {
  return optionsApi
    .post("v1/settings/email-template/validate", {
      templateBodyBase64,
      templateType,
    })
    .then((res) => res.data?.data);
}

export interface EmailTemplateVariable {
  variable: string;
  exampleValue: string;
}

export async function getEmailTemplateVariables(
  templateType: string
): Promise<EmailTemplateVariable[]> {
  return optionsApi
    .get("v1/settings/email-template/variables", { params: { templateType } })
    .then((res) => res.data.data);
}

export async function sendTestEmail(templateType: string): Promise<void> {
  return optionsApi
    .post("v1/settings/email-template/send-test", { templateType })
    .then((res) => res.data?.data);
}

export async function validateEmailTemplate(
  templateBodyBase64: string,
  templateType: string
): Promise<void> {
  return optionsApi
    .post("v1/settings/email-template/validate", {
      templateBodyBase64,
      templateType,
    })
    .then((res) => res.data?.data);
}

export interface EmailTemplateAsset {
  id: string;
  s3Path: string;
  name: string;
}

export async function getEmailTemplateAssets(): Promise<EmailTemplateAsset[]> {
  return optionsApi
    .get("v1/template/email-template-assets")
    .then((res) => res.data?.data ?? []);
}

export async function uploadEmailTemplateAsset(
  file: File
): Promise<EmailTemplateAsset> {
  const formData = new FormData();
  formData.append("file", file);
  return optionsApi
    .post("v1/template/email-template-assets", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data?.data);
}

export async function deleteEmailTemplateAsset(assetId: string): Promise<void> {
  return optionsApi
    .delete(`v1/template/email-template-assets/${assetId}`)
    .then(() => undefined);
}
